import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  CardContent,
  Input,
  Textarea,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Avatar,
  Snackbar,
  FormLabel,
  CssBaseline,
} from '@mui/joy';
import {
  Paper,
  Container,
  Typography as MuiTypography,
} from '@mui/material';
import {
  Save as SaveIcon,
  Upload as UploadIcon,
  Info as InfoIcon,
  Palette as PaletteIcon,
  Wallpaper as WallpaperIcon,
  Payment as PaymentIcon,
  Phone as PhoneIcon,
  Chat as ChatIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { 
  getSiteSettings, 
  updateSiteSettings, 
  getAllSettings,
  updateSettingById,
  uploadSiteIcon,
  uploadWelcomeBackground
} from '../../main/API/settings';
import PermissionGate from '../../main/components/PermissionGate';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import { DirectionalMaterialThemeProvider } from '../../main/theme/DirectionalMaterialThemeProvider';
import { translateAxiosErrorMessage } from '../../main/utils/masterApiError';


export default function SettingsScreen() {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();

  const getCategoryTitle = useCallback((name) => {
    const keyByName = {
      'Payment settings': 'catPayment',
      'Main Twilio Settings': 'catTwilio',
      'Content SIDs for WhatsApp messages': 'catWhatsAppSids',
      'WhatsApp chat bot System Messages (Arabic)': 'catWhatsAppChatbot',
    };
    const k = keyByName[name];
    return k ? t(`settingsPages.hub.${k}`) : name;
  }, [t]);

  const getSubsectionTitle = useCallback((name) => {
    if (!name) return null;
    const keyByName = {
      'PayMob Settings': 'subPaymob',
      'Thawani Settings': 'subThawani',
    };
    const k = keyByName[name];
    return k ? t(`settingsPages.hub.${k}`) : name;
  }, [t]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initialLoad, setInitialLoad] = useState(false);
  
  // Notification states
  const [successNotification, setSuccessNotification] = useState(false);
  const [failedNotification, setFailedNotification] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [siteSettings, setSiteSettings] = useState({
    site_name: '',
    site_name_ar: '',
    site_name_zh: '',
    site_icon: '',
    welcome_background: '',
    site_description: '',
    contact_email: '',
    contact_phone: '',
    address: '',
  });
  // Site icon upload states
  const [siteIconFile, setSiteIconFile] = useState(null);
  const [siteIconPreview, setSiteIconPreview] = useState(null);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  
  // Welcome background upload states
  const [welcomeBgFile, setWelcomeBgFile] = useState(null);
  const [welcomeBgPreview, setWelcomeBgPreview] = useState(null);
  const [uploadingWelcomeBg, setUploadingWelcomeBg] = useState(false);
  
  // Individual settings state
  const [allSettings, setAllSettings] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [editingValues, setEditingValues] = useState({}); // Track which settings are being edited
  const [savingSections, setSavingSections] = useState({}); // Track which sections are being saved (format: "categoryName-subsectionName")

  const loadSiteSettings = useCallback(async () => {
    console.log('SettingsScreen: loadSiteSettings called');
    if (!hasPermission(PERMISSIONS.SETTINGS_VIEW_PUBLIC)) {
      return;
    }
    setLoading(true);
    try {
      const settings = await getSiteSettings();
      setSiteSettings(prev => ({ ...prev, ...settings }));
      // Set preview if site_icon exists
      if (settings.site_icon) {
        setSiteIconPreview(settings.site_icon);
      }
      // Set preview if welcome_background exists
      if (settings.welcome_background) {
        setWelcomeBgPreview(settings.welcome_background);
      }
    } catch (error) {
      console.error('Failed to load site settings:', error);
      setResponseMessage(t('settingsPages.common.siteLoadFailed'));
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  }, [hasPermission, t]);

  const loadAllSettings = useCallback(async () => {
    console.log('SettingsScreen: loadAllSettings called');
    // Check permission before making API call
    if (!hasPermission(PERMISSIONS.SETTINGS_LIST)) {
      console.warn('User does not have permission to access settings');
      setAllSettings([]);
      return;
    }

    setSettingsLoading(true);
    try {
      const settings = await getAllSettings();
      setAllSettings(Array.isArray(settings) ? settings : []);
    } catch (error) {
      console.error('Failed to load settings:', error);
      setResponseMessage(t('settingsPages.common.loadFailed'));
      setFailedNotification(true);
      setAllSettings([]);
    } finally {
      setSettingsLoading(false);
    }
  }, [hasPermission, t]);

  // Load site settings on component mount
  useEffect(() => {
    if (!initialLoad) {
      console.log('SettingsScreen: Initial load starting');
      setInitialLoad(true);
      loadSiteSettings();
      loadAllSettings(); // Load individual settings
    }
  }, [initialLoad, loadSiteSettings, loadAllSettings]);

  const handleSiteSettingsChange = (field, value) => {
    setSiteSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSiteSettings = async () => {
    if (!hasPermission(PERMISSIONS.SETTINGS_UPDATE_SITE)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }
    setSaving(true);
    try {
      // Use bulk update API for site settings
      const dataToUpdate = {
        site_name: siteSettings.site_name || '',
        site_name_ar: siteSettings.site_name_ar || '',
        site_name_zh: siteSettings.site_name_zh || '',
        ...(siteSettings.site_icon && { site_icon: siteSettings.site_icon }),
        ...(siteSettings.welcome_background && { welcome_background: siteSettings.welcome_background })
      };
      await updateSiteSettings(dataToUpdate);
      setResponseMessage(t('settingsPages.common.siteUpdatedSuccess'));
      setSuccessNotification(true);
      // Reload settings to get updated data
      await loadSiteSettings();
    } catch (error) {
      console.error('Failed to update site settings:', error);
      setResponseMessage(translateAxiosErrorMessage(error, t) || t('settingsPages.common.siteUpdateFailed'));
      setFailedNotification(true);
    } finally {
      setSaving(false);
    }
  };

  // Helper function to format setting names for better readability
  const formatSettingName = (name) => {
    if (!name) return '';
    
    // Common acronyms that should stay uppercase
    const acronyms = ['API', 'SID', 'URL', 'HMAC', 'SMS', 'ID', 'UID', 'UUID'];
    
    // Split by underscore and format each part
    const parts = name.split('_').map(part => {
      const upperPart = part.toUpperCase();
      
      // Check if it's a known acronym
      if (acronyms.includes(upperPart)) {
        return upperPart;
      }
      
      // Capitalize first letter, lowercase the rest
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    });
    
    return parts.join(' ');
  };

  // Helper function to get icon and colors for each category
  const getCategoryIcon = (categoryName) => {
    const iconConfigs = {
      'Payment settings': {
        icon: PaymentIcon,
        bgColor: '#FEF3C7',
        iconColor: '#D97706',
      },
      'Main Twilio Settings': {
        icon: PhoneIcon,
        bgColor: '#DBEAFE',
        iconColor: '#2563EB',
      },
      'Content SIDs for WhatsApp messages': {
        icon: ChatIcon,
        bgColor: '#D1FAE5',
        iconColor: '#059669',
      },
      'WhatsApp chat bot System Messages (Arabic)': {
        icon: WhatsAppIcon,
        bgColor: '#DCFCE7',
        iconColor: '#25D366',
      },
    };
    
    return iconConfigs[categoryName] || {
      icon: InfoIcon,
      bgColor: '#F3F4F6',
      iconColor: '#6B7280',
    };
  };

  // Helper function to categorize settings into sections
  const categorizeSettings = (settings) => {
    // Define allowed settings and their categories
    const allowedSettings = {
      // Payment settings - PayMob Settings
      'PAYMOB_HMAC_SECRET': { category: 'Payment settings', subsection: 'PayMob Settings' },
      'PAYMOB_PUBLIC_KEY': { category: 'Payment settings', subsection: 'PayMob Settings' },
      'PAYMOB_SECRET_KEY': { category: 'Payment settings', subsection: 'PayMob Settings' },
      'PAYMOB_API_KEY': { category: 'Payment settings', subsection: 'PayMob Settings' },
      
      // Payment settings - Thawani Settings
      'THAWANI_WEBHOOK_SECRET_KEY': { category: 'Payment settings', subsection: 'Thawani Settings' },
      'THAWANI_SECRET_KEY': { category: 'Payment settings', subsection: 'Thawani Settings' },
      'THAWANI_PUBLIC_KEY': { category: 'Payment settings', subsection: 'Thawani Settings' },
      
      // Main Twilio Settings
      'TWILIO_ACCOUNT_SID': { category: 'Main Twilio Settings', subsection: null },
      'TWILIO_AUTH_TOKEN': { category: 'Main Twilio Settings', subsection: null },
      'TWILIO_WHATSAPP_FROM': { category: 'Main Twilio Settings', subsection: null },
      'MESSAGING_SERVICE_SID': { category: 'Main Twilio Settings', subsection: null },
      'CHANGE_DELIVERY_LOCATION_CONTENT_SID': { category: 'Main Twilio Settings', subsection: null },
      'MENU_CONTENT_SID': { category: 'Main Twilio Settings', subsection: null },
      'INVOICE_CONTENT_SID': { category: 'Main Twilio Settings', subsection: null },
      
      // Content SIDs for WhatsApp messages
      'SHIPMENT_OUT_FOR_DELIVERY_CONTENT_SID': { category: 'Content SIDs for WhatsApp messages', subsection: null },
      'SHIPMENT_DELIVERED_CONTENT_SID': { category: 'Content SIDs for WhatsApp messages', subsection: null },
      'SHIPMENT_SHIPPED_CONTENT_SID': { category: 'Content SIDs for WhatsApp messages', subsection: null },
      'SHIPMENT_SHIPPING_SOON_CONTENT_SID': { category: 'Content SIDs for WhatsApp messages', subsection: null },
      'SHIPMENT_ARRIVED_CONTENT_SID': { category: 'Content SIDs for WhatsApp messages', subsection: null },
      'SHIPMENT_DELETED_CONTENT_SID': { category: 'Content SIDs for WhatsApp messages', subsection: null },
      'STORE_CONTENT_SID': { category: 'Content SIDs for WhatsApp messages', subsection: null },
      
      // WhatsApp chat bot System Messages (Arabic)
      'SOON_REPLY_MSG': { category: 'WhatsApp chat bot System Messages (Arabic)', subsection: null },
      'ERROR_MSG': { category: 'WhatsApp chat bot System Messages (Arabic)', subsection: null },
      'BRANCH_SAVED_MSG': { category: 'WhatsApp chat bot System Messages (Arabic)', subsection: null },
      'HOME_LOCATION_SAVED_MSG': { category: 'WhatsApp chat bot System Messages (Arabic)', subsection: null },
      'REPLY_ANY_MSG': { category: 'WhatsApp chat bot System Messages (Arabic)', subsection: null },
      'HOME_LOCATION_MSG': { category: 'WhatsApp chat bot System Messages (Arabic)', subsection: null },
      'CHOOSE_BRANCH_MSG': { category: 'WhatsApp chat bot System Messages (Arabic)', subsection: null },
      'SORTING_CENTER_MSG': { category: 'WhatsApp chat bot System Messages (Arabic)', subsection: null },
      'LOCATION_URL': { category: 'WhatsApp chat bot System Messages (Arabic)', subsection: null },
    };

    // Filter and categorize settings
    const categorized = {};
    
    settings.forEach(setting => {
      const settingName = setting.name;
      const settingInfo = allowedSettings[settingName];
      
      // Only include allowed settings
      if (settingInfo) {
        const categoryKey = settingInfo.category;
        const subsectionKey = settingInfo.subsection || 'main';
        
        if (!categorized[categoryKey]) {
          categorized[categoryKey] = {};
        }
        
        if (!categorized[categoryKey][subsectionKey]) {
          categorized[categoryKey][subsectionKey] = [];
        }
        
        categorized[categoryKey][subsectionKey].push(setting);
      }
    });

    // Convert to array format with subsections
    const result = [];
    
    // Define the order of categories
    const categoryOrder = [
      'Payment settings',
      'Main Twilio Settings',
      'Content SIDs for WhatsApp messages',
      'WhatsApp chat bot System Messages (Arabic)'
    ];

    categoryOrder.forEach(categoryName => {
      if (categorized[categoryName]) {
        const categoryData = {
          name: categoryName,
          subsections: []
        };

        // Add subsections in order
        const subsections = categorized[categoryName];
        
        // For Payment settings, add PayMob first, then Thawani
        if (categoryName === 'Payment settings') {
          if (subsections['PayMob Settings']) {
            categoryData.subsections.push({
              name: 'PayMob Settings',
              settings: subsections['PayMob Settings']
            });
          }
          if (subsections['Thawani Settings']) {
            categoryData.subsections.push({
              name: 'Thawani Settings',
              settings: subsections['Thawani Settings']
            });
          }
        } else {
          // For other categories, add main subsection
          if (subsections['main']) {
            categoryData.subsections.push({
              name: null, // No subsection name
              settings: subsections['main']
            });
          }
        }

        if (categoryData.subsections.length > 0) {
          result.push(categoryData);
        }
      }
    });

    return result;
  };

  // Handle setting value change
  const handleSettingValueChange = (settingId, value) => {
    setEditingValues(prev => ({
      ...prev,
      [settingId]: value
    }));
  };

  // Handle save all settings in a subsection
  const handleSaveSection = async (categoryName, subsectionName, settings) => {
    if (!hasPermission(PERMISSIONS.SETTINGS_UPDATE_DETAIL)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return Promise.resolve();
    }
    const sectionKey = `${categoryName}-${subsectionName || 'main'}`;
    
    // Check if there are any changes in this section
    const hasChanges = settings.some(setting => {
      const editedValue = editingValues[setting.id];
      return editedValue !== undefined && editedValue !== setting.value;
    });

    if (!hasChanges) {
      setResponseMessage(t('settingsPages.common.noChangesToSave'));
      setFailedNotification(true);
      return Promise.resolve();
    }

    setSavingSections(prev => ({ ...prev, [sectionKey]: true }));
    
    try {
      // Save all settings in this section
      const savePromises = settings.map(async (setting) => {
        const newValue = editingValues[setting.id] !== undefined 
          ? editingValues[setting.id] 
          : setting.value;

        // Only update if value has changed
        if (newValue !== setting.value) {
          const settingData = {
            name: setting.name,
            value: newValue,
            description: setting.description || ''
          };
          return updateSettingById(setting.id, settingData);
        }
        return Promise.resolve();
      });

      await Promise.all(savePromises);
      
      const sectionDisplayName = subsectionName ? getSubsectionTitle(subsectionName) : getCategoryTitle(categoryName);
      setResponseMessage(t('settingsPages.common.sectionUpdatedSuccess', { section: sectionDisplayName }));
      setSuccessNotification(true);
      
      // Create a map of updated values for quick lookup
      const updatedValuesMap = {};
      settings.forEach(setting => {
        const editedValue = editingValues[setting.id];
        if (editedValue !== undefined) {
          updatedValuesMap[setting.id] = editedValue;
        }
      });
      
      // Update local state directly without reloading
      setAllSettings(prevSettings => {
        return prevSettings.map(setting => {
          // If this setting was updated, use the new value
          if (setting.id in updatedValuesMap) {
            return {
              ...setting,
              value: updatedValuesMap[setting.id]
            };
          }
          return setting;
        });
      });
      
      // Clear the editing values for all settings in this section
      setEditingValues(prev => {
        const updated = { ...prev };
        settings.forEach(setting => {
          delete updated[setting.id];
        });
        return updated;
      });
      
      return Promise.resolve();
    } catch (error) {
      console.error('Failed to save settings:', error);
      setResponseMessage(translateAxiosErrorMessage(error, t) || t('settingsPages.common.saveFailed'));
      setFailedNotification(true);
      return Promise.reject(error);
    } finally {
      setSavingSections(prev => {
        const updated = { ...prev };
        delete updated[sectionKey];
        return updated;
      });
    }
  };

  // Handle site icon file selection
  const handleSiteIconFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml', 'image/x-icon', 'image/vnd.microsoft.icon'];
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.ico'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      setResponseMessage(t('settingsPages.common.invalidImageIcon'));
      setFailedNotification(true);
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setResponseMessage(t('settingsPages.common.fileTooLarge5mb'));
      setFailedNotification(true);
      return;
    }

    setSiteIconFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setSiteIconPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Handle site icon upload
  const handleUploadSiteIcon = async () => {
    if (!hasPermission(PERMISSIONS.SETTINGS_FILES_UPLOAD)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }
    if (!siteIconFile) {
      setResponseMessage(t('settingsPages.common.selectFileFirst'));
      setFailedNotification(true);
      return;
    }

    setUploadingIcon(true);
    setResponseMessage('');
    
    try {
      const result = await uploadSiteIcon(siteIconFile);
      
      // Update site settings with the returned URL
      setSiteSettings(prev => ({
        ...prev,
        site_icon: result.url || result.site_icon
      }));
      
      setResponseMessage(t('settingsPages.common.siteIconUploadSuccess'));
      setSuccessNotification(true);
      
      // Update favicon immediately
      const favicon = document.querySelector('link[rel="icon"]');
      if (favicon) {
        favicon.href = result.url || result.site_icon;
      }
      
      // Reset file selection after successful upload
      setSiteIconFile(null);
      
      // Reload settings to get the updated icon URL
      await loadSiteSettings();
      
      // Trigger settings update event for other components
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('siteSettingsUpdated'));
      }
    } catch (error) {
      console.error('Failed to upload site icon:', error);
      setResponseMessage(error.message || t('settingsPages.common.siteIconUploadFailed'));
      setFailedNotification(true);
    } finally {
      setUploadingIcon(false);
    }
  };

  // Handle welcome background file selection
  const handleWelcomeBgFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      setResponseMessage(t('settingsPages.common.invalidImageBg'));
      setFailedNotification(true);
      return;
    }

    // Validate file size (max 10MB for background images)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setResponseMessage(t('settingsPages.common.fileTooLarge10mb'));
      setFailedNotification(true);
      return;
    }

    setWelcomeBgFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setWelcomeBgPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Handle welcome background upload
  const handleUploadWelcomeBackground = async () => {
    if (!hasPermission(PERMISSIONS.SETTINGS_FILES_UPLOAD)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }
    if (!welcomeBgFile) {
      setResponseMessage(t('settingsPages.common.selectFileFirst'));
      setFailedNotification(true);
      return;
    }

    setUploadingWelcomeBg(true);
    setResponseMessage('');
    
    try {
      const result = await uploadWelcomeBackground(welcomeBgFile);
      
      // Update site settings with the returned URL
      setSiteSettings(prev => ({
        ...prev,
        welcome_background: result.url || result.welcome_background
      }));
      
      setResponseMessage(t('settingsPages.common.welcomeBgUploadSuccess'));
      setSuccessNotification(true);
      
      // Reset file selection after successful upload
      setWelcomeBgFile(null);
      
      // Reload settings to get the updated background URL
      await loadSiteSettings();
      
      // Trigger settings update event for other components
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('siteSettingsUpdated'));
      }
    } catch (error) {
      console.error('Failed to upload welcome background:', error);
      setResponseMessage(error.message || t('settingsPages.common.welcomeBgUploadFailed'));
      setFailedNotification(true);
    } finally {
      setUploadingWelcomeBg(false);
    }
  };

  return (
    <DirectionalMaterialThemeProvider>
      <CssBaseline />
      <Box sx={{ minHeight: "100vh", bgcolor: "#FAFAFA" }}>
        <Box component="main" sx={{ overflow: "auto", pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
          <Container maxWidth="xl">
            <Box sx={{ mb: 4 }}>
              <MuiTypography variant="h4" sx={{ fontWeight: 700, color: "#1F2937", mb: 1 }}>
                {t('settingsPages.hub.pageTitle')}
              </MuiTypography>
              <MuiTypography variant="body1" color="#6B7280" sx={{ fontSize: '1.1rem' }}>
                {t('settingsPages.hub.pageSubtitle')}
              </MuiTypography>
            </Box>

            {/* Site Settings Section */}
            <Paper 
              elevation={0} 
              sx={{ 
                mb: 4,
                borderRadius: 3,
                border: '1px solid #E5E7EB',
                background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <MuiTypography variant="h5" sx={{ fontWeight: 700, color: '#1F2937', mb: 1 }}>
                    {t('settingsPages.hub.siteSectionTitle')}
                  </MuiTypography>
                  <MuiTypography variant="body2" color="#6B7280">
                    {t('settingsPages.hub.siteSectionSubtitle')}
                  </MuiTypography>
                </Box>
                
                <Grid container spacing={3}>
                  {/* Site Information Card */}
                  <Grid item xs={12} md={4}>
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        height: '100%', 
                        borderRadius: 3, 
                        border: '1px solid #E5E7EB',
                        background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': { 
                          transform: 'translateY(-2px)',
                          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                          border: '1px solid #D1D5DB'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              bgcolor: '#EEF2FF',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2
                            }}
                          >
                            <InfoIcon sx={{ color: '#4F46E5', fontSize: 24 }} />
                          </Box>
                          <Typography variant="subtitle2" color="#6B7280" sx={{ fontWeight: 500 }}>
                            {t('settingsPages.hub.siteInformation')}
                          </Typography>
                        </Box>
                    
                    <Box sx={{ mb: 3 }}>
                      <FormLabel sx={{ mb: 1, fontWeight: 500 }}>{t('settingsPages.hub.siteNameEn')}</FormLabel>
                      <Input
                        fullWidth
                        value={siteSettings.site_name || ''}
                        onChange={(e) => handleSiteSettingsChange('site_name', e.target.value)}
                        placeholder={t('settingsPages.hub.siteNameEnPlaceholder')}
                        slotProps={{ input: { maxLength: 255 } }}
                        sx={{ mt: 1 }}
                      />
                      <Typography level="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                        {t('settingsPages.hub.siteNameEnHint')}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <FormLabel sx={{ mb: 1, fontWeight: 500 }}>{t('settingsPages.hub.siteNameAr')}</FormLabel>
                      <Input
                        fullWidth
                        value={siteSettings.site_name_ar || ''}
                        onChange={(e) => handleSiteSettingsChange('site_name_ar', e.target.value)}
                        placeholder={t('settingsPages.hub.siteNameArPlaceholder')}
                        slotProps={{ input: { maxLength: 255, dir: 'rtl' } }}
                        sx={{ mt: 1 }}
                      />
                      <Typography level="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                        {t('settingsPages.hub.siteNameArHint')}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <FormLabel sx={{ mb: 1, fontWeight: 500 }}>{t('settingsPages.hub.siteNameZh')}</FormLabel>
                      <Input
                        fullWidth
                        value={siteSettings.site_name_zh || ''}
                        onChange={(e) => handleSiteSettingsChange('site_name_zh', e.target.value)}
                        placeholder={t('settingsPages.hub.siteNameZhPlaceholder')}
                        slotProps={{ input: { maxLength: 255 } }}
                        sx={{ mt: 1 }}
                      />
                      <Typography level="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                        {t('settingsPages.hub.siteNameZhHint')}
                      </Typography>
                    </Box>
                    
                    <PermissionGate permissions={[PERMISSIONS.SETTINGS_UPDATE_SITE]}>
                      <Button
                        variant="contained"
                        startDecorator={saving ? <CircularProgress sx={{ width: 20, height: 20 }} /> : <SaveIcon />}
                        onClick={handleSaveSiteSettings}
                        disabled={saving || loading}
                        fullWidth
                        sx={{ mt: 'auto' }}
                      >
                        {saving ? t('settingsPages.common.saving') : t('settingsPages.hub.saveSettings')}
                      </Button>
                    </PermissionGate>
                      </CardContent>
                    </Paper>
                  </Grid>

                  {/* Site Icon Card */}
                  <Grid item xs={12} md={4}>
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        height: '100%', 
                        borderRadius: 3, 
                        border: '1px solid #E5E7EB',
                        background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': { 
                          transform: 'translateY(-2px)',
                          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                          border: '1px solid #D1D5DB'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              bgcolor: '#ECFDF5',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2
                            }}
                          >
                            <PaletteIcon sx={{ color: '#10B981', fontSize: 24 }} />
                          </Box>
                          <Typography variant="subtitle2" color="#6B7280" sx={{ fontWeight: 500 }}>
                            {t('settingsPages.hub.siteIcon')}
                          </Typography>
                        </Box>
                    
                    <PermissionGate permissions={[PERMISSIONS.SETTINGS_FILES_UPLOAD]}>
                      <Box sx={{ mb: 2 }}>
                        <FormLabel sx={{ mb: 1, fontWeight: 500 }}>{t('settingsPages.hub.uploadSiteIcon')}</FormLabel>
                        <input
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/svg+xml,image/x-icon,image/vnd.microsoft.icon"
                          style={{ display: 'none' }}
                          id="site-icon-upload"
                          type="file"
                          onChange={handleSiteIconFileChange}
                          disabled={uploadingIcon}
                        />
                        <label htmlFor="site-icon-upload">
                          <Button
                            variant="outlined"
                            component="span"
                            startDecorator={<UploadIcon />}
                            fullWidth
                            disabled={uploadingIcon}
                            sx={{ mt: 1 }}
                          >
                            {siteIconFile ? siteIconFile.name : t('settingsPages.hub.chooseImageFile')}
                          </Button>
                        </label>
                        <Typography level="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                          {t('settingsPages.hub.iconFormatsHint')}
                        </Typography>
                      </Box>
                    </PermissionGate>
                    
                    {/* Preview */}
                    {siteIconPreview && (
                      <Box sx={{ 
                        mt: 2, 
                        mb: 2, 
                        textAlign: 'center',
                        p: 2,
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <Avatar
                          src={siteIconPreview}
                          sx={{ width: 80, height: 80, mx: 'auto', mb: 1 }}
                        />
                        {siteIconFile && (
                          <Typography level="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                            {siteIconFile.name}
                          </Typography>
                        )}
                        {siteIconFile && (
                          <Typography level="body3" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                            {(siteIconFile.size / 1024).toFixed(2)} KB
                          </Typography>
                        )}
                      </Box>
                    )}
                    
                    {/* Upload Button */}
                    {siteIconFile && (
                      <PermissionGate permissions={[PERMISSIONS.SETTINGS_FILES_UPLOAD]}>
                        <Button
                          variant="contained"
                          color="success"
                          startDecorator={uploadingIcon ? <CircularProgress sx={{ width: 20, height: 20 }} /> : <UploadIcon />}
                          onClick={handleUploadSiteIcon}
                          disabled={uploadingIcon}
                          fullWidth
                          sx={{ mt: 2 }}
                        >
                          {uploadingIcon ? t('settingsPages.common.uploading') : t('settingsPages.hub.uploadIcon')}
                        </Button>
                      </PermissionGate>
                    )}
                    
                    {/* Current Icon Display */}
                    {siteSettings.site_icon && !siteIconFile && (
                      <Box sx={{ 
                        mt: 2, 
                        p: 1.5, 
                        bgcolor: 'grey.50', 
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <Typography level="body3" sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                          {t('settingsPages.hub.currentIcon')}
                        </Typography>
                        <Input
                          fullWidth
                          value={siteSettings.site_icon}
                          onChange={(e) => handleSiteSettingsChange('site_icon', e.target.value)}
                          size="sm"
                          readOnly
                          sx={{ 
                            '& input': {
                              fontSize: '0.75rem',
                              fontFamily: 'monospace',
                              bgcolor: 'background.body'
                            }
                          }}
                        />
                      </Box>
                    )}
                      </CardContent>
                    </Paper>
                  </Grid>

                  {/* Welcome Background Card */}
                  <Grid item xs={12} md={4}>
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        height: '100%', 
                        borderRadius: 3, 
                        border: '1px solid #E5E7EB',
                        background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': { 
                          transform: 'translateY(-2px)',
                          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                          border: '1px solid #D1D5DB'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              bgcolor: '#FFFBEB',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2
                            }}
                          >
                            <WallpaperIcon sx={{ color: '#F59E0B', fontSize: 24 }} />
                          </Box>
                          <Typography variant="subtitle2" color="#6B7280" sx={{ fontWeight: 500 }}>
                            {t('settingsPages.hub.welcomeBackground')}
                          </Typography>
                        </Box>
                    
                    <PermissionGate permissions={[PERMISSIONS.SETTINGS_FILES_UPLOAD]}>
                      <Box sx={{ mb: 2 }}>
                        <FormLabel sx={{ mb: 1, fontWeight: 500 }}>{t('settingsPages.hub.uploadBackground')}</FormLabel>
                        <input
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/svg+xml,image/webp"
                          style={{ display: 'none' }}
                          id="welcome-bg-upload"
                          type="file"
                          onChange={handleWelcomeBgFileChange}
                          disabled={uploadingWelcomeBg}
                        />
                        <label htmlFor="welcome-bg-upload">
                          <Button
                            variant="outlined"
                            component="span"
                            startDecorator={<UploadIcon />}
                            fullWidth
                            disabled={uploadingWelcomeBg}
                            sx={{ mt: 1 }}
                          >
                            {welcomeBgFile ? welcomeBgFile.name : t('settingsPages.hub.chooseBackgroundImage')}
                          </Button>
                        </label>
                        <Typography level="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                          {t('settingsPages.hub.bgFormatsHint')}
                        </Typography>
                      </Box>
                    </PermissionGate>
                    
                    {/* Preview */}
                    {welcomeBgPreview && (
                      <Box sx={{ 
                        mt: 2, 
                        mb: 2,
                        p: 1.5,
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <img
                          src={welcomeBgPreview}
                          alt={t('settingsPages.hub.welcomeBgPreviewAlt')}
                          style={{
                            width: '100%',
                            height: '150px',
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #e0e0e0'
                          }}
                        />
                        {welcomeBgFile && (
                          <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                            <Typography level="body2" sx={{ color: 'text.secondary' }}>
                              {welcomeBgFile.name}
                            </Typography>
                            <Typography level="body3" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                              {(welcomeBgFile.size / 1024 / 1024).toFixed(2)} MB
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                    
                    {/* Upload Button */}
                    {welcomeBgFile && (
                      <PermissionGate permissions={[PERMISSIONS.SETTINGS_FILES_UPLOAD]}>
                        <Button
                          variant="contained"
                          color="warning"
                          startDecorator={uploadingWelcomeBg ? <CircularProgress sx={{ width: 20, height: 20 }} /> : <UploadIcon />}
                          onClick={handleUploadWelcomeBackground}
                          disabled={uploadingWelcomeBg}
                          fullWidth
                          sx={{ mt: 2 }}
                        >
                          {uploadingWelcomeBg ? t('settingsPages.common.uploading') : t('settingsPages.hub.uploadBackgroundBtn')}
                        </Button>
                      </PermissionGate>
                    )}
                    
                    {/* Current Background Display */}
                    {siteSettings.welcome_background && !welcomeBgFile && (
                      <Box sx={{ 
                        mt: 2, 
                        p: 1.5, 
                        bgcolor: 'grey.50', 
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <Typography level="body3" sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                          {t('settingsPages.hub.currentBackground')}
                        </Typography>
                        <Input
                          fullWidth
                          value={siteSettings.welcome_background}
                          onChange={(e) => handleSiteSettingsChange('welcome_background', e.target.value)}
                          size="sm"
                          readOnly
                          sx={{ 
                            '& input': {
                              fontSize: '0.75rem',
                              fontFamily: 'monospace',
                              bgcolor: 'background.body'
                            }
                          }}
                        />
                      </Box>
                    )}
                      </CardContent>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Paper>

            {/* Individual Settings Section */}
            <PermissionGate permissions={[PERMISSIONS.SETTINGS_LIST]}>
              <Paper 
                elevation={0} 
                sx={{ 
                  mb: 4,
                  borderRadius: 3,
                  border: '1px solid #E5E7EB',
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ mb: 3 }}>
                    <MuiTypography variant="h5" sx={{ fontWeight: 700, color: '#1F2937', mb: 1 }}>
                      {t('settingsPages.hub.individualTitle')}
                    </MuiTypography>
                    <MuiTypography variant="body2" color="#6B7280">
                      {t('settingsPages.hub.individualSubtitle')}
                    </MuiTypography>
                  </Box>

                {settingsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : !Array.isArray(allSettings) || allSettings.length === 0 ? (
                  <Alert severity="info">
                    {t('settingsPages.hub.noIndividualSettings')}
                  </Alert>
                ) : (
                  <Box>
                    {categorizeSettings(allSettings).map((category, categoryIndex) => (
                      <Paper
                        key={category.name}
                        elevation={0}
                        sx={{
                          mb: categoryIndex < categorizeSettings(allSettings).length - 1 ? 4 : 0,
                          p: 3,
                          borderRadius: 3,
                          border: '1px solid #E5E7EB',
                          background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                        }}
                      >
                        <Box sx={{ mb: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            {(() => {
                              const iconConfig = getCategoryIcon(category.name);
                              const IconComponent = iconConfig.icon;
                              return (
                                <Box
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    bgcolor: iconConfig.bgColor,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mr: 1.5
                                  }}
                                >
                                  <IconComponent sx={{ color: iconConfig.iconColor, fontSize: 24 }} />
                                </Box>
                              );
                            })()}
                            <MuiTypography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
                              {getCategoryTitle(category.name)}
                            </MuiTypography>
                          </Box>
                          <Divider />
                        </Box>
                        
                        {category.subsections.map((subsection, subsectionIndex) => {
                          const sectionKey = `${category.name}-${subsection.name || 'main'}`;
                          const isSaving = savingSections[sectionKey] || false;
                          const hasChanges = subsection.settings.some(setting => {
                            const editedValue = editingValues[setting.id];
                            return editedValue !== undefined && editedValue !== setting.value;
                          });

                          return (
                            <Box key={subsectionIndex}>
                              {subsectionIndex > 0 && (
                                <Divider sx={{ my: 3 }} />
                              )}
                              
                              {subsection.name && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#4B5563', fontSize: '0.95rem' }}>
                                    {getSubsectionTitle(subsection.name)}
                                  </Typography>
                                </Box>
                              )}
                            
                              <Grid container spacing={2}>
                                {subsection.settings.map((setting) => {
                                  const currentValue = editingValues[setting.id] !== undefined 
                                    ? editingValues[setting.id] 
                                    : (setting.value || '');
                                  
                                  // Use textarea for WhatsApp chat bot System Messages (Arabic) section
                                  const useTextarea = category.name === 'WhatsApp chat bot System Messages (Arabic)';

                                  return (
                                    <Grid item xs={12} key={setting.id}>
                                      <Paper 
                                        elevation={0}
                                        sx={{
                                          p: 2,
                                          borderRadius: 2,
                                          border: '1px solid #F3F4F6',
                                          background: '#FFFFFF',
                                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                          '&:hover': {
                                            transform: 'translateY(-2px)',
                                            border: '1px solid #E5E7EB',
                                            boxShadow: '0 8px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -5px rgba(0, 0, 0, 0.04)',
                                          }
                                        }}
                                      >
                                        <Box sx={{ mb: 1.5 }}>
                                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1F2937', mb: 0.5 }}>
                                            {formatSettingName(setting.name)}
                                          </Typography>
                                          {setting.description && (
                                            <Typography variant="body2" color="#6B7280" sx={{ fontSize: '0.875rem' }}>
                                              {setting.description}
                                            </Typography>
                                          )}
                                        </Box>
                                        
                                        {useTextarea ? (
                                          <Textarea
                                            fullWidth
                                            value={currentValue}
                                            onChange={(e) => handleSettingValueChange(setting.id, e.target.value)}
                                            placeholder={t('settingsPages.common.enterSettingValue')}
                                            disabled={isSaving}
                                            minRows={3}
                                            maxRows={10}
                                            sx={{ 
                                              fontFamily: 'monospace',
                                              fontSize: '0.875rem'
                                            }}
                                          />
                                        ) : (
                                          <Input
                                            fullWidth
                                            value={currentValue}
                                            onChange={(e) => handleSettingValueChange(setting.id, e.target.value)}
                                            placeholder={t('settingsPages.common.enterSettingValue')}
                                            disabled={isSaving}
                                            sx={{ 
                                              '& input': {
                                                fontFamily: 'monospace',
                                                fontSize: '0.875rem'
                                              }
                                            }}
                                          />
                                        )}
                                      </Paper>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                              
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                                <PermissionGate permissions={[PERMISSIONS.SETTINGS_MANAGE]}>
                                  <Button
                                    type="button"
                                    variant="contained"
                                    size="sm"
                                    startDecorator={isSaving ? <CircularProgress sx={{ width: 16, height: 16 }} /> : <SaveIcon />}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      // Store current scroll position
                                      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
                                      handleSaveSection(category.name, subsection.name, subsection.settings).then(() => {
                                        // Restore scroll position after React re-renders
                                        requestAnimationFrame(() => {
                                          window.scrollTo(0, scrollPosition);
                                        });
                                      });
                                    }}
                                    disabled={isSaving || !hasChanges}
                                    sx={{ minWidth: 120 }}
                                  >
                                    {isSaving ? t('settingsPages.common.saving') : t('actions.save')}
                                  </Button>
                                </PermissionGate>
                              </Box>
                            </Box>
                          );
                        })}
                      </Paper>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Paper>
          </PermissionGate>


      {/* Success Notification */}
      <Snackbar
        autoHideDuration={3000}
        open={successNotification}
        variant="soft"
        color="success"
        onClose={(event, reason) => {
          if (reason === 'clickaway') {
            return;
          }
          setSuccessNotification(false);
        }}
      >
        {responseMessage}
      </Snackbar>

      {/* Error Notification */}
      <Snackbar
        autoHideDuration={3000}
        open={failedNotification}
        variant="soft"
        color="danger"
        onClose={(event, reason) => {
          if (reason === 'clickaway') {
            return;
          }
          setFailedNotification(false);
        }}
      >
        {responseMessage}
      </Snackbar>
          </Container>
        </Box>
      </Box>
    </DirectionalMaterialThemeProvider>
  );
}
